import * as React from "react"

import { cn } from "@/lib/utils"
import {
  STICKY_GLASS_CHROME_CLASSNAME,
  STICKY_GLASS_HEADER_SHADOW_CLASSNAME,
  findOverflowAncestor,
  observeElementScrollEdges,
  type ElementScrollEdges,
} from "@/lib/sticky-glass"

export const TABLE_HEADER_STICKY_CLASSNAME =
  "sticky top-0 z-10 [&_tr]:border-b"

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full">
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
))
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => {
  const localRef = React.useRef<HTMLTableSectionElement | null>(null)
  const [scrollEdges, setScrollEdges] = React.useState<ElementScrollEdges>({
    canScrollUp: false,
    canScrollDown: false,
  })

  React.useEffect(() => {
    const thead = localRef.current
    if (!thead) {
      return
    }
    const scrollParent = findOverflowAncestor(thead)
    if (!scrollParent) {
      setScrollEdges({ canScrollUp: false, canScrollDown: false })
      return
    }
    return observeElementScrollEdges(scrollParent, setScrollEdges)
  }, [])

  const setRefs = React.useCallback(
    (node: HTMLTableSectionElement | null) => {
      localRef.current = node
      if (typeof ref === "function") {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
    },
    [ref]
  )

  return (
    <thead
      ref={setRefs}
      className={cn(
        TABLE_HEADER_STICKY_CLASSNAME,
        scrollEdges.canScrollUp
          ? cn(
              STICKY_GLASS_CHROME_CLASSNAME,
              STICKY_GLASS_HEADER_SHADOW_CLASSNAME,
              "[&_th]:bg-transparent [&_tr]:bg-transparent"
            )
          : "bg-card [&_th]:bg-card",
        className
      )}
      {...props}
    />
  )
})
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, onClick, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors data-[state=selected]:bg-muted",
      onClick && "hover:bg-muted",
      className
    )}
    onClick={onClick}
    {...props}
  />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props}
  />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
